// src/components/shared/FollowButton.js - MEJORADO
import React, { useState, useEffect, useCallback } from 'react';
import { FiUserPlus, FiUserCheck, FiUser } from 'react-icons/fi';
import { followService } from '../../services/followService';
import supabase from '../../supabase';

const FollowButton = ({ 
  profileId, 
  profileType = 'auto', // ✅ 'auto' detecta automáticamente, 'technician' | 'company'
  variant = 'button', // 'icon' | 'button' | 'compact'
  size = 'md', // 'sm' | 'md' | 'lg'
  className = '',
  onFollow = () => {},
  onUnfollow = () => {},
  // Nuevas props para optimización
  initialFollowState = null, // Estado inicial para evitar consulta
  onFollowChange = null, // Callback para notificar cambios al parent
  disabled = false,
  showCount = false // Mostrar contador de seguidores
}) => {
  const [isFollowing, setIsFollowing] = useState(initialFollowState ?? false);
  const [loading, setLoading] = useState(initialFollowState === null);
  const [processing, setProcessing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [detectedType, setDetectedType] = useState(profileType);

  // ✅ DETECTAR TIPO DE PERFIL AUTOMÁTICAMENTE
  useEffect(() => {
    if (profileType === 'auto') {
      detectProfileType();
    } else {
      setDetectedType(profileType);
    }
  }, [profileId, profileType]);

  const detectProfileType = async () => {
    try {
      // Primero verificar en user_profiles
      const { data: userProfile, error: userError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', profileId)
        .maybeSingle();

      if (!userError && userProfile) {
        setDetectedType('technician');
        return;
      }

      // Si no está en user_profiles, verificar en company_profiles
      const { data: companyProfile, error: companyError } = await supabase
        .from('company_profiles')
        .select('id')
        .eq('id', profileId)
        .maybeSingle();

      if (!companyError && companyProfile) {
        setDetectedType('company');
        return;
      }

      // Si no se encuentra en ninguna, asumir technician por defecto
      setDetectedType('technician');
    } catch (error) {
      console.error('Error detectando tipo de perfil:', error);
      setDetectedType('technician');
    }
  };

  // Solo verificar estado si no se proporcionó estado inicial
  useEffect(() => {
    if (initialFollowState !== null) {
      setIsFollowing(initialFollowState);
      setLoading(false);
      return;
    }

    checkFollowStatus();
  }, [profileId, initialFollowState]);

  // Actualizar estado cuando cambie el prop inicial
  useEffect(() => {
    if (initialFollowState !== null) {
      setIsFollowing(initialFollowState);
    }
  }, [initialFollowState]);

  // Obtener contador de seguidores si se requiere
  useEffect(() => {
    if (showCount) {
      getFollowersCount();
    }
  }, [profileId, showCount]);

  const checkFollowStatus = useCallback(async () => {
    if (initialFollowState !== null) return; // Skip si ya tenemos estado inicial

    try {
      setLoading(true);
      const following = await followService.isFollowing(profileId);
      setIsFollowing(following);
    } catch (error) {
      console.error('Error verificando seguimiento:', error);
      setIsFollowing(false);
    } finally {
      setLoading(false);
    }
  }, [profileId, initialFollowState]);

  const getFollowersCount = useCallback(async () => {
    try {
      const count = await followService.getFollowersCount(profileId);
      setFollowersCount(count);
    } catch (error) {
      console.error('Error obteniendo contador de seguidores:', error);
    }
  }, [profileId]);

  const handleToggleFollow = useCallback(async (e) => {
    e.stopPropagation();
    
    if (processing || disabled) return;
    setProcessing(true);

    try {
      const newFollowState = !isFollowing;
      
      // Actualización optimista
      setIsFollowing(newFollowState);
      
      // Actualizar contador optimistamente
      if (showCount) {
        setFollowersCount(prev => newFollowState ? prev + 1 : Math.max(0, prev - 1));
      }
      
      // Notificar al componente padre inmediatamente
      if (onFollowChange) {
        onFollowChange(profileId, newFollowState);
      }

      if (isFollowing) {
        await followService.unfollowProfile(profileId);
        onUnfollow(profileId);
      } else {
        // ✅ USAR EL TIPO DETECTADO EN LUGAR DEL PROP
        console.log('🔄 Following with type:', detectedType);
        await followService.followProfile(profileId, detectedType);
        onFollow(profileId);
      }

    } catch (error) {
      console.error('Error al cambiar seguimiento:', error);
      // Rollback en caso de error
      setIsFollowing(!isFollowing);
      if (showCount) {
        setFollowersCount(prev => !isFollowing ? prev + 1 : Math.max(0, prev - 1));
      }
      if (onFollowChange) {
        onFollowChange(profileId, !isFollowing);
      }
    } finally {
      setProcessing(false);
    }
  }, [isFollowing, processing, profileId, detectedType, onFollow, onUnfollow, onFollowChange, disabled, showCount]);

  // Configuraciones de tamaño
  const sizeConfig = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'p-1.5',
      iconSize: 14,
      textClass: 'text-xs'
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'p-2',
      iconSize: 16,
      textClass: 'text-sm'
    },
    lg: {
      button: 'px-4 py-3 text-base',
      icon: 'p-3',
      iconSize: 18,
      textClass: 'text-base'
    }
  };

  const config = sizeConfig[size];

  if (loading) {
    return (
      <div className={`${variant === 'icon' ? config.icon : config.button} ${className} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-gray-400"></div>
      </div>
    );
  }

  const Icon = isFollowing ? FiUserCheck : FiUserPlus;
  const title = isFollowing ? 'Dejar de seguir' : 'Seguir';
  const text = isFollowing ? 'Siguiendo' : 'Seguir';

  // Estilo base del botón
  const baseClasses = `
    transition-all duration-200 border backdrop-blur-sm font-medium
    ${disabled || processing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}
    ${isFollowing 
      ? 'bg-primary-600/90 text-white border-primary-500 hover:bg-primary-700/90' 
      : 'bg-dark-700/90 hover:bg-primary-600/90 text-gray-300 hover:text-white border-dark-600 hover:border-primary-500'
    }
  `;

  if (variant === 'icon') {
    return (
      <button
        onClick={handleToggleFollow}
        disabled={processing || disabled}
        className={`${config.icon} rounded-full ${baseClasses} ${className}`}
        title={title}
      >
        <Icon size={config.iconSize} />
      </button>
    );
  }

  if (variant === 'compact') {
    return (
      <button
        onClick={handleToggleFollow}
        disabled={processing || disabled}
        className={`${config.button} rounded-lg flex items-center ${baseClasses} ${className}`}
        title={title}
      >
        <Icon size={config.iconSize} />
        {showCount && followersCount > 0 && (
          <span className="ml-1 font-medium">{followersCount}</span>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleToggleFollow}
      disabled={processing || disabled}
      className={`${config.button} rounded-lg flex items-center ${baseClasses} ${className}`}
    >
      <Icon size={config.iconSize} className="mr-2 flex-shrink-0" />
      <span className="flex-grow">
        {processing ? 'Procesando...' : text}
      </span>
      {showCount && followersCount > 0 && (
        <span className="ml-2 text-xs opacity-75">
          {followersCount}
        </span>
      )}
    </button>
  );
};

export default FollowButton;